/**
 * useV4AccountState — the ONE bridge from live app state to the pure
 * `accountState` model. Every v4 door (home slot, You card, guide slot, capture
 * sheet) reads its rendering from `accountState.ts` selectors fed by THIS hook,
 * so there is a single place that decides "given who she is, what does each door
 * show?" (spec rule 4 — no surface re-decides for itself).
 *
 * Inputs, all existing:
 *   - auth (onAuthStateChanged)      → signedIn + the bootstrap gate
 *   - useRecipients                  → boards (newest first, emoji FE-resolved)
 *   - useCaptureRecord               → capturedEmail + captureStatus (3-state)
 *   - useAlerts                      → alertCount (gated to signed-in here)
 *   - isInAppBrowser                 → webview vs normal
 */

import { useEffect, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';

import { useAuth } from '../firebase/FirebaseContext';
import { useRecipients } from '../hooks/useRecipients';
import { useAlerts } from '../hooks/useAlerts';
import { resolveRecipientEmoji } from '../lib/resolveRecipientEmoji';
import { isInAppBrowser } from '../lib/inAppBrowser';
import { useCaptureRecord } from './captureRecord';
import type { AccountState, BoardSummary } from './accountState';

export interface V4AccountStateResult {
  state: AccountState;
  /**
   * Auth AND recipients have settled. Hosts hold a neutral placeholder until
   * this is true, so the door never flashes "Sign in" for a user who is already
   * signed in or already has a board (the anti-flash discipline, architecture.md).
   */
  ready: boolean;
}

/** Firestore Timestamp → epoch seconds, tolerant of the admin/web shapes. */
function createdSeconds(r: { createdAt?: { seconds?: number; _seconds?: number } }): number {
  return r.createdAt?.seconds ?? r.createdAt?._seconds ?? 0;
}

export function useV4AccountState(): V4AccountStateResult {
  const auth = useAuth();
  // Synchronous seed from persisted auth (skips the placeholder for a user
  // already restored from IndexedDB), then the listener confirms.
  const [user, setUser] = useState<User | null>(() => auth.currentUser);
  const [authReady, setAuthReady] = useState<boolean>(() => auth.currentUser !== null);
  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        setUser(u);
        setAuthReady(true);
      }),
    [auth],
  );

  const { recipients, hydrated } = useRecipients();
  const capture = useCaptureRecord();
  const { count: alertCount } = useAlerts();

  const signedIn = !!user && !user.isAnonymous;

  const boards: BoardSummary[] = [...recipients]
    .filter((r) => !r.archivedAt)
    .sort((a, b) => createdSeconds(b) - createdSeconds(a)) // newest first (naming rule)
    .map((r) => ({
      id: r.recipientId,
      name: r.name || 'your person',
      emoji: resolveRecipientEmoji(r.emoji, r.relationship),
      isMe: r.isMe,
    }));

  const state: AccountState = {
    signedIn,
    boards,
    capturedEmail: capture.email,
    captureStatus: capture.status,
    // Identity feeds the avatar initials + the dropdown header. Only a
    // permanent account has one; an anon user's (usually absent) name must not
    // drive the avatar, so it stays null and the person-icon fallback renders.
    displayName: signedIn ? user?.displayName ?? null : null,
    accountEmail: signedIn ? user?.email ?? null : null,
    browser: isInAppBrowser() ? 'webview' : 'normal',
    // Signed-out visitors never see alerts — front-end gate only (the nightly
    // job keeps materialising them). accountState's selectors also gate on
    // signedIn; this is belt-and-suspenders so a stray anon alert can't leak.
    alertCount: signedIn ? alertCount : 0,
  };

  return { state, ready: authReady && hydrated };
}
